import { Fragment, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, ExternalLink, Plus, Search, Check } from 'lucide-react';
import { marketingWebAPI } from '../../../api/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import {
  FILTROS_POSICION, Variacion, aplicarFiltro, fmtNum, fmtPct, fmtPos, ordenar, urlCorta,
} from './seoUtils';

const PASO = 100;

function Cabecera({ label, clave, orden, onOrden, className = '' }) {
  const activa = orden.clave === clave;
  const Icono = orden.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onOrden(clave)}
        className={`inline-flex items-center gap-1 hover:text-gray-900 ${activa ? 'text-gray-900 font-semibold' : ''}`}
      >
        {label}
        {activa && <Icono className="w-3 h-3" />}
      </button>
    </TableHead>
  );
}

function CeldasMetricas({ r }) {
  return (
    <>
      <TableCell className="text-right text-sm whitespace-nowrap">
        {fmtNum(r.clicks)} <span className="ml-1"><Variacion valor={r.var_clicks} nueva={r.nueva} /></span>
      </TableCell>
      <TableCell className="text-right text-sm whitespace-nowrap">
        {fmtNum(r.impressions)} {!r.nueva && <span className="ml-1"><Variacion valor={r.var_impressions} /></span>}
      </TableCell>
      <TableCell className="text-right text-sm">{fmtPct(r.ctr)}</TableCell>
      <TableCell className="text-right text-sm whitespace-nowrap">
        {fmtPos(r.position)} {!r.nueva && <span className="ml-1"><Variacion valor={r.var_position} tipo="posicion" /></span>}
      </TableCell>
    </>
  );
}

/**
 * Tabla de consultas o páginas del periodo, con comparación, orden, buscador y filtros.
 * tipo: "consultas" | "paginas"
 */
export default function TablaSeo({ tipo, filas, periodo, keywordsSeguidas, onSeguir }) {
  const [texto, setTexto] = useState('');
  const [filtro, setFiltro] = useState('todas');
  const [orden, setOrden] = useState({ clave: 'clicks', dir: 'desc' });
  const [visibles, setVisibles] = useState(PASO);
  const [abiertas, setAbiertas] = useState({});
  const [consultasPagina, setConsultasPagina] = useState({});

  const onOrden = (clave) => {
    setOrden((o) => {
      if (o.clave === clave) return { clave, dir: o.dir === 'asc' ? 'desc' : 'asc' };
      // Posición y texto empiezan de menor a mayor; el resto de mayor a menor
      return { clave, dir: clave === 'position' || clave === 'key' ? 'asc' : 'desc' };
    });
  };

  const filtradas = useMemo(() => {
    let out = aplicarFiltro(filas || [], filtro);
    if (texto) {
      const t = texto.toLowerCase();
      out = out.filter((r) => (r.key || '').toLowerCase().includes(t));
    }
    return ordenar(out, orden.clave, orden.dir);
  }, [filas, filtro, texto, orden]);

  const alternarPagina = async (url) => {
    const abierta = !abiertas[url];
    setAbiertas((a) => ({ ...a, [url]: abierta }));
    if (abierta && !consultasPagina[url]) {
      setConsultasPagina((c) => ({ ...c, [url]: { cargando: true, filas: [] } }));
      try {
        const res = await marketingWebAPI.seoPaginaConsultas({
          pagina: url, desde: periodo.desde, hasta: periodo.hasta, estado: periodo.estado,
        });
        setConsultasPagina((c) => ({ ...c, [url]: { cargando: false, filas: res.data.consultas || [] } }));
      } catch (e) {
        setConsultasPagina((c) => ({ ...c, [url]: { cargando: false, filas: [], error: true } }));
      }
    }
  };

  const esConsultas = tipo === 'consultas';

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {FILTROS_POSICION.map((f) => (
            <button
              key={f.id}
              type="button"
              title={f.hint || ''}
              onClick={() => { setFiltro(f.id); setVisibles(PASO); }}
              className={`px-2.5 py-1 text-xs rounded-full border ${filtro === f.id ? 'bg-duetto-primary text-white border-duetto-primary' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-2 text-gray-400" />
          <input
            type="text"
            placeholder={esConsultas ? 'Buscar consulta' : 'Buscar dirección'}
            value={texto}
            onChange={(e) => { setTexto(e.target.value); setVisibles(PASO); }}
            className="pl-9 pr-3 py-1.5 text-sm border rounded-md w-64"
          />
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-2">
        {fmtNum(filtradas.length)} {esConsultas ? 'consultas' : 'páginas'}
        {periodo.periodoAnterior && (
          <> · las flechas comparan con el periodo anterior ({periodo.periodoAnterior})</>
        )}
      </p>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <Cabecera label={esConsultas ? 'Consulta' : 'Página'} clave="key" orden={orden} onOrden={onOrden} />
              <Cabecera label="Clics" clave="clicks" orden={orden} onOrden={onOrden} className="text-right" />
              <Cabecera label="Impresiones" clave="impressions" orden={orden} onOrden={onOrden} className="text-right" />
              <Cabecera label="CTR" clave="ctr" orden={orden} onOrden={onOrden} className="text-right" />
              <Cabecera label="Posición" clave="position" orden={orden} onOrden={onOrden} className="text-right" />
              {esConsultas && <TableHead className="w-24" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtradas.slice(0, visibles).map((r) => {
              if (esConsultas) {
                const seguida = keywordsSeguidas.has(r.key);
                return (
                  <TableRow key={r.key}>
                    <TableCell className="text-sm">{r.key}</TableCell>
                    <CeldasMetricas r={r} />
                    <TableCell className="text-right">
                      {seguida ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><Check className="w-3 h-3" />Objetivo</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onSeguir(r.key)}
                          title="Añadir a palabras clave objetivo"
                          className="inline-flex items-center gap-1 text-xs text-duetto-primary hover:underline"
                        >
                          <Plus className="w-3 h-3" />Seguir
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              }
              const abierta = !!abiertas[r.key];
              const det = consultasPagina[r.key];
              return (
                <Fragment key={r.key}>
                  <TableRow>
                    <TableCell className="text-xs font-mono">
                      <div className="flex items-center gap-1.5">
                        <button type="button" onClick={() => alternarPagina(r.key)} className="text-gray-500 hover:text-gray-900" title="Ver sus consultas">
                          {abierta ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                        <button type="button" onClick={() => alternarPagina(r.key)} className="hover:underline text-left">
                          {urlCorta(r.key)}
                        </button>
                        <a href={r.key} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-700" title="Abrir la página">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </TableCell>
                    <CeldasMetricas r={r} />
                  </TableRow>
                  {abierta && (
                    <TableRow className="bg-gray-50 hover:bg-gray-50">
                      <TableCell colSpan={5} className="py-2 pl-10">
                        {det?.cargando && <div className="text-xs text-gray-500">Cargando consultas…</div>}
                        {det?.error && <div className="text-xs text-red-600">No se han podido cargar las consultas de esta página.</div>}
                        {det && !det.cargando && !det.error && (
                          det.filas.length === 0 ? (
                            <div className="text-xs text-gray-500">Sin consultas en este periodo.</div>
                          ) : (
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="text-left font-medium py-1">Consulta</th>
                                  <th className="text-right font-medium">Clics</th>
                                  <th className="text-right font-medium">Impresiones</th>
                                  <th className="text-right font-medium">Posición</th>
                                  <th />
                                </tr>
                              </thead>
                              <tbody>
                                {det.filas.slice(0, 50).map((c) => (
                                  <tr key={c.key} className="border-t border-gray-200">
                                    <td className="py-1">{c.key}</td>
                                    <td className="text-right">{fmtNum(c.clicks)} <Variacion valor={c.var_clicks} nueva={c.nueva} /></td>
                                    <td className="text-right">{fmtNum(c.impressions)}</td>
                                    <td className="text-right">{fmtPos(c.position)} {!c.nueva && <Variacion valor={c.var_position} tipo="posicion" />}</td>
                                    <td className="text-right">
                                      {keywordsSeguidas.has(c.key) ? null : (
                                        <button type="button" onClick={() => onSeguir(c.key, r.key)} className="text-duetto-primary hover:underline">Seguir</button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
            {filtradas.length === 0 && (
              <TableRow>
                <TableCell colSpan={esConsultas ? 6 : 5} className="text-center text-gray-400 py-4">
                  {esConsultas ? 'Sin consultas con estos filtros' : 'Sin páginas con estos filtros'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {filtradas.length > visibles && (
        <div className="text-center mt-3">
          <button
            type="button"
            onClick={() => setVisibles((v) => v + PASO)}
            className="px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50"
          >
            Mostrar {Math.min(PASO, filtradas.length - visibles)} más de {fmtNum(filtradas.length - visibles)} restantes
          </button>
        </div>
      )}
    </div>
  );
}
